import type { CityConfig } from "@/pages/city-template/types";

export const yumaConfig: CityConfig = {
  city: "Yuma",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "yuma",
  route: "/cities/yuma",
  title: "Emergency & Expedited Passport Services Yuma AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Yuma, AZ — as fast as 24 hours. Fast Passport Center couriers to the Western Passport Center in Tucson, about 250 miles east. A+ BBB rated. Serving Marine Corps Air Station Yuma, Colorado River communities, and border region travelers at the CA-AZ-Mexico crossroads.",
  h1: "Emergency & Expedited Passport Services in Yuma, AZ",
  heroSubtitle: "Yuma sits at the crossroads of Arizona, California, and Mexico along the Colorado River, about 250 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a Marine Corps Air Station service member with overseas orders, a winter vegetable capital agribusiness traveler, or a Yuma County family with international vacation plans.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Yuma%20Arizona%20Colorado%20River%20waterfront%20at%20golden%20sunset%20with%20calm%20water%20reflections%20and%20desert%20landscape%2C%20historic%20downtown%20Yuma%20with%20southwestern%20architecture%2C%20Yuma%20Territorial%20Prison%20area%20with%20dramatic%20desert%20light%2C%20professional%20landscape%20photography%2C%20wide%20angle%20composition%20with%20river%20and%20desert%2C%20warm%20golden%20tones%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=yuma-hero-001&orientation=landscape",
  heroImageAlt: "Yuma Arizona Colorado River and desert landscape at golden sunset hour",
  geoPosition: "32.6927;-114.6277",
  geoPlacename: "Yuma, Arizona",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 250 miles from Yuma — about 4 hours via I-8 and I-10",
  },
  nearbyCitiesZips: [
    { name: "Yuma", zips: "85364, 85365, 85367" },
    { name: "Fortuna Foothills", zips: "85367" },
    { name: "Somerton", zips: "85350" },
    { name: "San Luis", zips: "85349" },
    { name: "Wellton", zips: "85356" },
    { name: "Gadsden", zips: "85336" },
    { name: "Tacna", zips: "85352" },
    { name: "Dateland", zips: "85333" },
    { name: "Winterhaven, CA", zips: "92283" },
    { name: "Blythe, CA", zips: "92225" },
    { name: "El Centro, CA", zips: "92243" },
    { name: "Calexico, CA", zips: "92231" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 250 miles from Yuma" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Border Region Hub", desc: "Serving Yuma, Somerton, San Luis, Fortuna Foothills, and the CA-AZ border region" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Yuma case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Expedited passport in Tucson AZ", route: "/cities/tucson" },
    { name: "Urgent passport in Scottsdale AZ", route: "/cities/scottsdale" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Expedited passport in Goodyear AZ", route: "/cities/goodyear" },
    { name: "Fast passport in Buckeye AZ", route: "/cities/buckeye" },
    { name: "Emergency passport in Bullhead City AZ", route: "/cities/bullhead-city" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Yuma remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Yuma sits along the I-8 corridor in Arizona's southwest corner, making overnight document shipping reliable from Yuma County and the border region.",
  fpcOfficeDistance: "Serving all of Yuma County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Yuma, Fortuna Foothills, Somerton, San Luis, and Yuma County. From Marine Corps Air Station service members to agribusiness professionals, we help Yuma County travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Yuma and nearby communities including Fortuna Foothills, Somerton, San Luis, Wellton, Gadsden, and the surrounding CA-AZ border region including Winterhaven, Blythe, El Centro, and Calexico.",
  howItWorksIntro: "Most Yuma travelers complete everything online and ship documents overnight. At about 250 miles from Tucson, Fast Passport Center provides fast courier turnaround for Yuma County residents — ideal for MCAS Yuma service members and border region families with international travel needs.",
  mistakesIntro: "Yuma travelers, including MCAS Yuma military personnel, agribusiness professionals, and border region residents, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Yuma and Yuma County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Yuma? Here are the most common ones we hear from Yuma County and border region travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for MCAS Yuma service members deploying or PCSing overseas",
        "Perfect for agribusiness professionals with international trade travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from YUM or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 250 miles to Tucson (4-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Yuma is about 250 miles from the Western Passport Center in Tucson. Our couriers deliver for rush processing, as fast as 24 hours.",
      bullets: ["Yuma County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Yuma adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Yuma and Yuma County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Yuma and Yuma County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Military family support"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Yuma, Fortuna Foothills, and Yuma County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Yuma County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Yuma and border region residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Yuma AZ", description: "Urgent passport processing for Yuma, AZ travelers via Western Passport Center courier — about 250 miles from Yuma." },
    { name: "Passport Renewal Yuma AZ", description: "Fast passport renewal for Yuma and Yuma County, Arizona residents." },
    { name: "New Passport Yuma AZ", description: "First-time passport application assistance for Yuma and border region residents." },
    { name: "Lost Passport Yuma AZ", description: "Passport replacement for lost or stolen passports for Yuma and Yuma County residents." },
    { name: "Child Passport Yuma AZ", description: "Minor passport services for Yuma and Yuma County families." },
    { name: "Name Change Passport Yuma AZ", description: "Passport name change service for Yuma, AZ residents." },
  ],
  testimonials: [
    {
      name: "Cpl. Michael D.",
      location: "MCAS Yuma",
      rating: 5,
      text: "Deployment orders to Japan with 12 days notice. My tourist passport was expired. Fast Passport Center handled everything — new passport in 4 days. Even though Yuma's 250 miles from Tucson, the courier turnaround was faster than I expected.",
      service: "Military Deployment Expedited",
    },
    {
      name: "Rosa M.",
      location: "Somerton, AZ",
      rating: 5,
      text: "Cross-border family with relatives in Mexicali. Needed passports for my three kids for a wedding in Guadalajara. Fast Passport Center helped with all three DS-11 applications and had them back in 6 days.",
      service: "Family Passports",
    },
    {
      name: "Ken W.",
      location: "Fortuna Foothills",
      rating: 5,
      text: "Agribusiness consultant with last-minute meetings in Chile. My passport was expiring in 2 months — didn't realize Chile requires 6 months validity. Fast Passport Center got me renewed in 3 days. Made my meetings.",
      service: "Expedited Renewal",
    },
    {
      name: "Laura S.",
      location: "Yuma, AZ",
      rating: 5,
      text: "Winter visitor (snowbird) from Canada. Needed a U.S. passport renewed for a cruise departing from Miami. Fast Passport Center processed everything remotely and had my passport to my Yuma winter home in 5 days.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "yum-1",
      name: "Yuma Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2222 S 4th Ave",
      city: "Yuma",
      state: "AZ",
      zip: "85364",
      phone: "(928) 343-9712",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Central Yuma",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Yuma. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "yum-2",
      name: "Yuma County Clerk of Superior Court",
      type: "County Clerk Passport Acceptance",
      address: "250 W 2nd St, Suite B",
      city: "Yuma",
      state: "AZ",
      zip: "85364",
      phone: "(928) 817-4235",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Yuma",
      website: "https://www.courts.yumacountyaz.gov/clerk",
      websiteLabel: "Yuma County Clerk",
      notes: "Accepts passport applications for Yuma County residents. Convenient downtown location.",
      icon: "ri-government-line",
    },
    {
      id: "yum-3",
      name: "Fortuna Foothills Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11611 S Foothills Blvd",
      city: "Yuma",
      state: "AZ",
      zip: "85367",
      phone: "(928) 342-8075",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10 miles east of downtown in Fortuna Foothills",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient alternative for Fortuna Foothills and eastern Yuma County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "yum-4",
      name: "San Luis Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "874 N Main St",
      city: "San Luis",
      state: "AZ",
      zip: "85349",
      phone: "(928) 627-2116",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles south of Yuma, US-Mexico border",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Luis, Gadsden, and southern Yuma County residents near the border.",
      icon: "ri-mail-line",
    },
    {
      id: "yum-5",
      name: "Somerton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 N Bingham Ave",
      city: "Somerton",
      state: "AZ",
      zip: "85350",
      phone: "(928) 627-8320",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles southwest of Yuma",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Somerton and western Yuma County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "yum-6",
      name: "Wellton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "28621 Oakland Ave",
      city: "Wellton",
      state: "AZ",
      zip: "85356",
      phone: "(928) 785-4310",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles east of Yuma",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Wellton, Tacna, and eastern Yuma County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "250 miles — 4-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking a 4-Hour Drive to Tucson Is the Solution",
      description: "Yuma is 250 miles from the Western Passport Center — about a 4-hour drive each way. And you still need an appointment. Walk-ins are turned away. An 8-hour round trip across the desert is not how you want to spend your day. We handle the appointment and delivery for you.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2x2 inches with a white background. CVS and Walgreens in Yuma are generally reliable, but always verify your photos meet the exact requirements — military ID photos use different specifications.",
    },
    {
      icon: "ri-shield-line",
      title: "MCAS Yuma Deployment Passport Confusion",
      description: "Marine Corps service members sometimes confuse official/no-fee passports (for deployment) with tourist passports (for personal leave travel). Make sure you know which type you need before starting the application process.",
    },
    {
      icon: "ri-file-list-line",
      title: "Name Change Documentation for Recently Married",
      description: "Yuma County sees many name changes after marriage. If your passport name doesn't match your ID, you'll need certified marriage certificates. Border region residents often miss this requirement for cross-border family travel.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Proof of Travel for Emergency Processing",
      description: "Emergency processing at the Western Passport Center requires confirmed travel documents. Have your flight itinerary, deployment orders, or cruise booking ready before reaching out.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Processing Time in Yuma's Remote Location",
      description: "Yuma's location in Arizona's far southwest corner means standard USPS processing can be slower than average. Plan for 8-11 weeks minimum with standard processing. Fast Passport Center's courier service eliminates the distance penalty.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Yuma, Fortuna Foothills, or Somerton." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — about 250 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Yuma or Yuma County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Yuma from a passport agency?",
      a: "Yuma is approximately 250 miles from the Western Passport Center in Tucson — about a 4-hour drive via I-8 and I-10. Yuma is also about 170 miles from the San Diego Passport Agency, but the Western Passport Center in Tucson is the designated Arizona agency. Fast Passport Center handles the courier delivery so you don't need to make either trip.",
    },
    {
      q: "Can MCAS Yuma Marines get fast passports for deployment or PCS?",
      a: "Yes. Marine Corps Air Station Yuma service members frequently need expedited tourist passports for overseas assignments and personal leave travel. We can process in 24–48 hours for emergencies or 2–9 business days for standard expedited service. Note: official/no-fee passports for deployment are handled through military channels.",
    },
    {
      q: "How fast can I get an emergency passport in Yuma?",
      a: "Emergency processing is typically 24–48 hours for Yuma residents. Our couriers deliver directly to the Western Passport Center in Tucson, about 250 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Yuma have its own passport agency?",
      a: "No. The closest regional passport agency is the Western Passport Center in Tucson, about 250 miles away. Yuma also has the San Diego Passport Agency about 170 miles west in California, but Arizona residents are typically served by the Tucson agency. Yuma has acceptance facilities at the Main Post Office and Yuma County Clerk.",
    },
    {
      q: "What about cross-border travelers from Yuma?",
      a: "Yuma's location at the CA-AZ-Mexico crossroads means many residents have family and business on both sides of the border. A valid U.S. passport is essential for legal border crossings. Fast Passport Center helps Yuma County residents get and renew passports fast — including for the entire family.",
    },
    {
      q: "Where do Yuma travelers fly internationally?",
      a: "Yuma travelers typically fly internationally through Yuma International Airport (YUM, with connections to PHX), Phoenix Sky Harbor International Airport (PHX, 185 miles east), or San Diego International Airport (SAN, 170 miles west). A valid U.S. passport is required for all international flights.",
    },
    {
      q: "How do I renew my passport in Yuma?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8-11 weeks).",
    },
    {
      q: "Where can I get passport photos in Yuma?",
      a: "CVS Pharmacy, Walgreens, and FedEx Office in Yuma offer passport photo services. The Yuma Main Post Office on S 4th Ave accepts applications but may not offer photos. Always verify your photos meet the exact 2x2 inch specification before submitting.",
    },
    {
      q: "What documents do I need for a first-time passport in Yuma?",
      a: "You'll need Form DS-11, a certified birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Yuma Main Post Office or Yuma County Clerk.",
    },
    {
      q: "Can you serve Somerton, San Luis, and the entire Yuma County?",
      a: "Yes. We serve all of Yuma County including Yuma, Fortuna Foothills, Somerton, San Luis, Wellton, Gadsden, and surrounding communities. We also serve the CA border region including Winterhaven, Blythe, El Centro, and Calexico. Documents ship overnight from any UPS location in the area.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Yuma", address: "2800 S 4th Ave, Yuma, AZ 85364", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Yuma", address: "2801 S 4th Ave, Yuma, AZ 85364", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Yuma", address: "1555 S Yuma Palms Pkwy, Yuma, AZ 85365", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Fortuna Foothills", address: "11225 N Frontage Rd, Yuma, AZ 85367", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Fortuna Foothills", address: "11427 S Fortuna Rd, Yuma, AZ 85367", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Yuma", address: "2900 S Pacific Ave, Ste B, Yuma, AZ 85365", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Yuma", address: "2165 E Gila Ridge Rd, Yuma, AZ 85365", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Yuma County residents. Primary Yuma MVD location.", featured: true },
    { name: "AZ MVD — San Luis", address: "663 N Main St, San Luis, AZ 85349", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves San Luis and southern Yuma County border region residents.", featured: false },
    { name: "CA DMV — El Centro", address: "233 N Imperial Ave, El Centro, CA 92243", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Convenient for Yuma-area residents who also need California DMV services.", featured: false },
  ],
  airports: [
    {
      code: "YUM",
      name: "Yuma International Airport",
      distance: "4 miles south of downtown Yuma",
      address: "2191 E 32nd St, Yuma, AZ 85365",
      note: "Yuma's local airport with connections to Phoenix Sky Harbor via American Airlines. Shared facility with MCAS Yuma. Passport required for international connections through PHX.",
      flightUrl: "https://www.yumaairport.com/",
      flightLabel: "YUM Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "185 miles east of Yuma",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 3 hours east via I-8 and I-10.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "170 miles west of Yuma",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "Major California international hub with extensive global connections. About 2.5 hours west via I-8. Alternative option for Yuma travelers heading to Pacific destinations.",
      flightUrl: "https://www.san.org/",
      flightLabel: "SAN Airport",
      featured: false,
    },
  ],
};